import circleBg from '@/assets/notifications/resolution-won-all/medallion-circle-bg.svg';
import trophy from '@/assets/notifications/resolution-won-all/medallion-trophy.png';
import confetti from '@/assets/notifications/resolution-won-all/confetti-sprite.png';
import { formatCurrency } from '@/lib/format-currency';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketDeepLinkId, marketTitle, metaNumber, metaString } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta, PopupLinkCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['resolution-won-all'];

/** Settlement amounts arrive in USD dollars → cents-based formatter. */
function money(dollars: number): string {
  return formatCurrency(Math.round(dollars * 100));
}

/**
 * MARKET_RESOLUTION · WON_ALL — every held position hit. Trophy medallion with a
 * decorative confetti layer, a big green settlement payout, and a celebratory
 * "You called it!". Matches `.arc111/figma/resolution-won-all/`.
 */
export function ResolutionWonAllPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const eventName = metaString(meta, 'eventName') ?? marketTitle(meta);
  const settlementAmount = metaNumber(meta, 'settlementAmount');
  const secondary = CTA.secondary;
  const hasMarket = marketDeepLinkId(meta) !== undefined;

  return (
    <>
      <div className="relative flex items-center justify-center">
        {/* Decorative confetti — bounded and shifted upward (toward the card top,
            per Figma) so it scatters around the trophy without bleeding down over
            the body copy when the intro line wraps. */}
        <img
          src={confetti}
          alt=""
          aria-hidden
          className="pointer-events-none absolute bottom-1/2 left-1/2 w-[300px] max-w-[80vw] -translate-x-1/2 translate-y-[35%] select-none"
        />
        <Medallion circleSrc={circleBg} illustrationSrc={trophy} tone="positive" alt="" />
      </div>

      <div className="flex flex-col items-center gap-2">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">You called it!</h2>

        {eventName && (
          <p className="text-xl text-ink">
            All of your positions in <span className="font-bold">{`“${eventName}”`}</span> hit
          </p>
        )}

        {settlementAmount !== undefined && (
          <>
            <p className="text-[46px] font-bold leading-none tracking-[-0.92px] text-success">
              {`+${money(settlementAmount)}`}
            </p>
            <p className="text-xl text-success">Added to your balance</p>
          </>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <PopupCta
          label={CTA.primary.label}
          width={320}
          onClick={() => onAction(CTA.primary.action)}
          testId="popup-cta-primary"
        />
        {secondary && hasMarket && (
          <PopupLinkCta label={secondary.label} onClick={() => onAction(secondary.action)} testId="popup-cta-secondary" />
        )}
      </div>
    </>
  );
}
